/**
 * TherapyMiniCard — tarjeta de "Terapias destacadas" del Inicio.
 *
 * Trasladada desde `src/pages/Home.tsx` (CEC-261012-58fb) para poder probarla
 * sola y bajar el tamaño de la página. Muestra la foto de la terapia en cuadro,
 * como las tarjetas de productos y obras; sin foto, el panel violeta con la
 * ilustración de antes.
 */
import { Link } from 'react-router-dom';

import { cn } from '../../lib/cn';
import { Routes } from '../../lib/routes';
import { TherapyPhoto } from '../therapies/TherapyPhoto';
import { TherapyArtwork, focusToIconKey } from '../therapies/sections/TherapyArtwork';
import type { FeaturedTherapy } from './api';
import { StarDot } from './ornaments';

export function TherapyMiniCard({ therapy }: { therapy: FeaturedTherapy }) {
  return (
    <Link
      to={Routes.therapyDetail(therapy.slug)}
      aria-label={`Ver ${therapy.title}`}
      className={cn(
        // Editorial-clásica: imagen arriba + cream abajo. Mismo esqueleto que
        // `ProductMiniCard` y `EventDarkCard` para coherencia visual del grid
        // del Home.
        'group flex h-full flex-col overflow-hidden rounded-lg',
        'border border-cream-200 bg-cream-50',
        'shadow-elev1 hover:shadow-elev2',
        'transition-all duration-2 ease-out hover:-translate-y-0.5',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-cream-100',
      )}
    >
      {/* ── Foto de la terapia en cuadro (CEC-261012-58fb). Sin foto: panel
          violeta con medallón crema e ilustración (`<TherapyArtwork>`); el
          medallón mantiene legibles los tonos lavanda sobre el violeta. */}
      <TherapyPhoto
        coverUrl={therapy.cover_url}
        title={therapy.title}
        className="aspect-square w-full bg-primary-900"
        fallback={
          <>
            {/* Ornamentos decorativos externos — halos + estrellas */}
            <div aria-hidden="true" className="pointer-events-none absolute inset-0">
              <div className="absolute -left-10 -top-10 h-32 w-32 rounded-pill bg-primary-500/25 blur-3xl" />
              <div className="absolute -right-10 -bottom-10 h-32 w-32 rounded-pill bg-gold-500/15 blur-3xl" />
              <StarDot className="absolute left-5 top-5 text-[10px] opacity-70" />
              <StarDot className="absolute right-6 bottom-6 text-[10px] opacity-55" />
              <StarDot className="absolute right-5 top-10 text-[8px] opacity-55" />
              <StarDot className="absolute left-6 bottom-9 text-[8px] opacity-50" />
            </div>
            {/* Medallón crema + ilustración compositiva */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className={cn(
                  'relative flex h-[136px] w-[136px] items-center justify-center overflow-hidden',
                  'rounded-pill bg-cream-50 shadow-elev1 ring-1 ring-gold-500/40',
                  'transition-transform duration-3 ease-out group-hover:scale-105',
                  'md:h-[152px] md:w-[152px]',
                )}
              >
                <TherapyArtwork iconKey={focusToIconKey(therapy.focus_tags)} />
              </div>
            </div>
          </>
        }
      />

      {/* ── Bloque texto: cream sólido ─────────────────────────────────── */}
      <div className="flex flex-1 flex-col items-center gap-2 p-5 text-center">
        <h3 className="font-display text-h4 text-primary-700">{therapy.title}</h3>
        {therapy.summary && (
          <p className="text-caption text-primary-900/75 line-clamp-2">{therapy.summary}</p>
        )}
      </div>
    </Link>
  );
}
